import { Box, Link as MLink, Stack } from "@mui/material";
import {
  IconBrandWhatsapp,
  IconCalendar,
  IconCreditCard,
  IconMapPin,
  IconUsers,
} from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Button from "../../../../components/ui/Button";
import SafeHtml from "../../../../components/ui/SafeHtml";
import Text from "../../../../components/ui/Text";
import { useToast } from "../../../../contexts/ToastContext";
import apiService from "../../../../services/api";
import { formatGameDateTime } from "../../../../utils/formatters";
import { formatTeamSize } from "../../../../utils/game";
import { HowToFindUsDialog } from "./HowToFindUsDialog";

export const GameHeader = ({ gameDetails }) => {
  const { showToast } = useToast();
  const [group, setGroup] = useState(null);

  const [isExpanded, setIsExpanded] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const contentRef = useRef(null);

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    if (gameDetails && gameDetails.group_id) {
      const getGroupData = async () => {
        try {
          const response = await apiService.fetchGroupById(
            gameDetails.group_id,
          );
          if (response.success) {
            setGroup(response.data);
          }
        } catch (error) {
          showToast(error.error, "error");
        }
      };
      getGroupData();
    }
  }, [gameDetails]);

  useEffect(() => {
    if (contentRef.current) {
      const fullHeight = contentRef.current.scrollHeight;
      setContentHeight(fullHeight);
      setShowReadMore(fullHeight > 80);
    }
  }, [gameDetails.description]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Text variant="pageHeader" sx={{ mb: 2 }}>
        {`${gameDetails.venue}`}
      </Text>
      <Text sx={{ display: "block", mb: 2, fontSize: "20px" }}>
        {`${formatTeamSize(gameDetails.players)} ${gameDetails.sport}`}
      </Text>

      {/* Description */}
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Box
          ref={contentRef}
          sx={{
            maxHeight: isExpanded ? `${contentHeight}px` : "80px",
            overflow: "hidden",
            position: "relative",
            transition: "max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <SafeHtml
            html={gameDetails.description}
            sx={{
              lineHeight: 1.5,
              fontSize: "14px",
              fontFamily: "inherit",
            }}
          />
          {!isExpanded && showReadMore && (
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "60px",
                background: "linear-gradient(to bottom, transparent, white)",
                transition: "opacity 0.3s ease",
                opacity: isExpanded ? 0 : 1,
              }}
            />
          )}
        </Box>
        {showReadMore && (
          <Button
            variant="tertiary"
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{
              mt: 1,
              p: 0,
              px: 1,
              fontSize: "14px",
              height: "auto",
              ml: "auto",
            }}
          >
            {isExpanded ? "Show less" : "Read more"}
          </Button>
        )}
      </Box>

      {/* Game Details Section */}
      <Stack spacing={2}>
        {/* Date and Time */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ flexShrink: 0 }}>
            <IconCalendar size={20} />
          </Box>
          <Text variant="paragraph">
            {formatGameDateTime(
              gameDetails?.datetime,
              gameDetails?.duration_minutes,
            )}
          </Text>
        </Box>

        {/* Price and Payment */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ flexShrink: 0 }}>
            <IconCreditCard size={20} />
          </Box>
          <Text variant="paragraph">
            €{gameDetails.amount_per_player.toFixed(2) || "Not set"}
            {/* Only show Revolut link if payment method is "myself" */}
            {gameDetails.use_revolut && gameDetails.revolut_link && (
              <MLink
                href={
                  gameDetails.revolut_link.startsWith("http")
                    ? gameDetails.revolut_link
                    : `https://${gameDetails.revolut_link}`
                }
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "#1890FF",
                  textDecoration: "none",
                  cursor: "pointer",
                }}
              >
                {` ${gameDetails.revolut_link}`}
              </MLink>
            )}
          </Text>
        </Box>

        {/* Group */}
        <Box sx={{ display: "flex", alignItems: "start", gap: 0.5 }}>
          <Box sx={{ flexShrink: 0 }}>
            <IconUsers size={20} />
          </Box>
          <MLink
            component={Link}
            to={`/group/${gameDetails.group_id}`}
            sx={{
              color: "#1890FF",
              textDecoration: "none",
              cursor: "pointer",
            }}
          >
            {group?.name || "..."}
          </MLink>
        </Box>

        {/* WhatsApp Link */}
        {gameDetails.whatsapp_link && (
          <Box sx={{ display: "flex", alignItems: "start", gap: 0.5 }}>
            <Box sx={{ flexShrink: 0 }}>
              <IconBrandWhatsapp size={20} />
            </Box>
            <MLink
              component={Link}
              to={gameDetails.whatsapp_link}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: "#1890FF",
                textDecoration: "none",
                cursor: "pointer",
                wordBreak: "break-all",
              }}
            >
              {gameDetails.whatsapp_link}
            </MLink>
          </Box>
        )}

        {/* Location */}
        <Box sx={{ display: "flex", alignItems: "start", gap: 0.5 }}>
          <Box sx={{ flexShrink: 0 }}>
            <IconMapPin size={20} />
          </Box>
          <MLink
            component={Link}
            to={
              gameDetails.location.place_id
                ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    gameDetails.venue || "Location",
                  )}&query_place_id=${gameDetails.location.place_id}`
                : `https://www.google.com/maps?q=${gameDetails.location.lat},${gameDetails.location.lng}`
            }
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: "#1890FF",
              textDecoration: "none",
              cursor: "pointer",
            }}
          >
            {gameDetails.location.address || "Not set"}
          </MLink>
        </Box>
        {gameDetails.how_to_find && (
          <Button
            variant="secondary"
            sx={{ height: "38px", borderColor: "#d9d9d9" }}
            onClick={() => setIsDialogOpen(true)}
          >
            How to find us?
          </Button>
        )}
      </Stack>
      <HowToFindUsDialog
        text={gameDetails.how_to_find}
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      />
    </Box>
  );
};
